import moment from "moment";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { CheckBox } from "../../components/reactHookFormFields/checkbox";
import { BtnComponent } from "../../components/button";
import Tooltip from "../../components/Tooltip";
import baseApiHelper from "../../services/baseApiHelper";
import { useGetMessageUnreadCountQuery } from "../../services/chatService";


function checkInStatusLabel(item) {
  if (!item.checked_in) return "Checked Out";
  if (item.checked_in_time && moment(item.checked_in_time).isValid()) {
    return `Checked In since ${moment(item.checked_in_time).format("MM/DD/YYYY hh:mm A")}`;
  }
  return "Checked In";
}

const DualListBoxLocations = ({ 
  filteredLocations = [], 
  locationChatProcessingId = null, 
  onSyncCheckinCheckout = () => {},
  isFetching = false,
  control = null,
  setValue = () => {},
}) => {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [localLocations, setLocalLocations] = useState([]);
  
  const prevFilteredLocationsRef = useRef(null);

  const { refetch: refetchUnreadCount } = useGetMessageUnreadCountQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  const refreshUnreadMessageCount = useCallback(async () => {
    baseApiHelper().invalidateTags(["UnreadMessageCount"]);
    await refetchUnreadCount();
  }, [refetchUnreadCount]);

  useEffect(() => {
    if (filteredLocations && 
        (!prevFilteredLocationsRef.current || 
         JSON.stringify(filteredLocations) !== JSON.stringify(prevFilteredLocationsRef.current))) {
      setLocalLocations(filteredLocations);
      prevFilteredLocationsRef.current = filteredLocations;
    }
  }, [filteredLocations]);


  const checkedOutLocations = localLocations?.filter(item => !item.checked_in) || [];
  const checkedInLocations = localLocations?.filter(item => item.checked_in) || [];

  const filteredCheckedOutLocations = checkedOutLocations.filter(item =>
    item.name.toLowerCase().includes(leftSearch.toLowerCase())
  );
  const filteredCheckedInLocations = checkedInLocations.filter(item =>
    item.name.toLowerCase().includes(rightSearch.toLowerCase())
  );

  const handleLocationSelect = useCallback((locationId, isSelected) => {
    if (isSelected) {
      setSelectedLocations(prev => [...prev, locationId]);
    } else {
      setSelectedLocations(prev => prev.filter(id => id !== locationId));
    }
  }, []);

  const clearAllSelections = useCallback(() => {
    selectedLocations.forEach(locationId => {
      setValue(`location_select_${locationId}`, false);
    });
    setSelectedLocations([]);
  }, [selectedLocations, setValue]);

  const LocationItem = ({ item, index }) => {
    const handleRowClick = () => {
      const isCurrentlySelected = selectedLocations.includes(item.id);
      handleLocationSelect(item.id, !isCurrentlySelected);
      setValue(`location_select_${item.id}`, !isCurrentlySelected);
    };

    return (
      <div
        key={item.id}
        className="border-b-[0.5px] py-3 px-3 hover:bg-gray-50 transition-colors flex items-start sm:items-center gap-3 cursor-pointer min-w-0"
        onClick={handleRowClick}
      >
        <div className="flex-shrink-0 pt-0.5 sm:pt-0">
          <CheckBox
            name={`location_select_${item.id}`}
            control={control}
            checked={selectedLocations.includes(item.id)}
            onChange={(e) => handleLocationSelect(item.id, e.target.checked)}
          />
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {item.is_group ? (
            <>
              <div className="flex w-full min-w-0 justify-center">
                <div className="inline-flex max-w-full min-w-0 items-center gap-2">
                  <UserGroupIcon
                    className="block h-5 w-5 shrink-0 text-emerald-700"
                    aria-hidden
                  />
                  <span className="min-w-0 text-center text-sm font-medium leading-snug text-black break-words">
                    {item.name}
                  </span>
                </div>
              </div>
              <div className="break-words text-center text-xs text-gray-600">
                {(item.locations || [])
                  .map((loc) => loc?.name)
                  .filter(Boolean)
                  .join(", ") || "No locations"}
              </div>
              <div className="break-words text-center text-xs text-gray-600">
                {checkInStatusLabel(item)}
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-black break-words">{item.name}</div>
              <div className="text-xs text-gray-600 break-words">{checkInStatusLabel(item)}</div>
            </>
          )}
        </div>
        {locationChatProcessingId === "syncing" && (
          <div className="flex-shrink-0">
            <span className="text-xs text-gray-500">Processing...</span>
          </div>
        )}
      </div>
    );
  };

  const EmptyState = ({ message }) => (
    <div className="flex items-center justify-center h-32 text-gray-500">
      <p className="text-sm">{message}</p>
    </div>
  );

  const handleMoveToCheckedIn = useCallback(async () => {
    const selectedCheckedOutLocations = checkedOutLocations.filter(location => 
      selectedLocations.includes(location.id)
    );
    
    clearAllSelections();
    
    const updatedLocations = localLocations.map(location => {
      if (selectedLocations.includes(location.id) && !location.checked_in) {
        return { 
          ...location, 
          checked_in: true, 
          checked_in_time: new Date().toISOString() 
        };
      }
      return location;
    });
    
    setLocalLocations(updatedLocations);
    
    // const currentCheckedInIds = checkedInLocations.map(location => parseInt(location.id, 10));
    // const newCheckedInIds = selectedCheckedOutLocations.map(location => parseInt(location.id, 10));
    const allCheckedInSelections = [...checkedInLocations, ...selectedCheckedOutLocations];
    
    try {
      await onSyncCheckinCheckout({allCheckedInSelections: allCheckedInSelections, previousCheckedInSelections: checkedInLocations});
      await refreshUnreadMessageCount();
    } catch (error) {
      setLocalLocations(filteredLocations);
      console.error("Failed to sync locations:", error);
    }
  }, [checkedOutLocations, selectedLocations, clearAllSelections, localLocations, checkedInLocations, onSyncCheckinCheckout, filteredLocations, refreshUnreadMessageCount]);

  const handleMoveToCheckedOut = useCallback(async () => {
    const selectedCheckedInLocations = checkedInLocations.filter(location => 
      selectedLocations.includes(location.id)
    );
    
    clearAllSelections();
    
    const updatedLocations = localLocations.map(location => {
      if (selectedLocations.includes(location.id) && location.checked_in) {
        return { 
          ...location, 
          checked_in: false, 
          checked_in_time: null 
        };
      }
      return location;
    });
    
    setLocalLocations(updatedLocations);
    
    const selectedCheckedInIds = selectedCheckedInLocations.map(location => parseInt(location.id, 10));
    const remainingCheckedInSelections = checkedInLocations
      .filter(location => !selectedCheckedInIds.includes(parseInt(location.id, 10)))
    try {
      await onSyncCheckinCheckout({allCheckedInSelections: remainingCheckedInSelections, previousCheckedInSelections: checkedInLocations});
      await refreshUnreadMessageCount();
    } catch (error) {
      setLocalLocations(filteredLocations);
      console.error("Failed to sync locations:", error);
    }
  }, [checkedInLocations, selectedLocations, clearAllSelections, localLocations, onSyncCheckinCheckout, filteredLocations, refreshUnreadMessageCount]);

  const hasSelectedCheckedOut = checkedOutLocations.some(location => 
    selectedLocations.includes(location.id)
  );
  const hasSelectedCheckedIn = checkedInLocations.some(location => 
    selectedLocations.includes(location.id)
  );

  useEffect(() => {
    const allCheckedOutSelected = filteredCheckedOutLocations.length > 0 && 
      filteredCheckedOutLocations.every(location => selectedLocations.includes(location.id));
    setValue(`select_all_checked_out_locations`, allCheckedOutSelected);

    const allCheckedInSelected = filteredCheckedInLocations.length > 0 && 
      filteredCheckedInLocations.every(location => selectedLocations.includes(location.id));
    setValue(`select_all_checked_in_locations`, allCheckedInSelected);
  }, [selectedLocations, filteredCheckedOutLocations, filteredCheckedInLocations, setValue]);
  
  const handleSelectAllCheckedOutLocations = (checked, locations) => {
    const locationIds = locations.map(location => location.id);
    if (checked) {
      setSelectedLocations(prev => {
        const newSelection = [...prev];
        locationIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
          setValue(`location_select_${id}`, true);
        });
        return newSelection;
      });
    } else {
      setSelectedLocations(prev => prev.filter(id => !locationIds.includes(id)));
      locationIds.forEach(id => {
        setValue(`location_select_${id}`, false);
      });
    }
  };

  const handleSelectAllCheckedInLocations = (checked, locations) => {
    const locationIds = locations.map(location => location.id);
    if (checked) {
      setSelectedLocations(prev => {
        const newSelection = [...prev];
        locationIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
          setValue(`location_select_${id}`, true);
        });
        return newSelection;
      });
    } else {
      setSelectedLocations(prev => prev.filter(id => !locationIds.includes(id)));
      locationIds.forEach(id => {
        setValue(`location_select_${id}`, false);
      });
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 md:h-[500px] h-auto min-h-0 w-full">
      {/* Checked Out Locations */}
      <div className="flex flex-col flex-1 min-w-0 w-full border rounded-lg bg-white min-h-[220px] md:min-h-0">
        <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b rounded-t-lg flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Checked Out Locations ({filteredCheckedOutLocations.length})
          </h3>
          <input
            type="text"
            value={leftSearch}
            onChange={(e) => setLeftSearch(e.target.value)}
            placeholder="Search checked out..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
            aria-label="Search checked out locations"
          />
        </div>
        <div className="px-3 py-2 border-b flex-shrink-0">
          <CheckBox
            title="Select All"
            name={`select_all_checked_out_locations`}
            control={control}
            onChange={(e) => handleSelectAllCheckedOutLocations(e.target.checked, filteredCheckedOutLocations)}
            checked={
              filteredCheckedOutLocations.length > 0 &&
              filteredCheckedOutLocations.every(location => selectedLocations.includes(location.id))
            }
          />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 ">
          {filteredCheckedOutLocations.length > 0 ? (
            filteredCheckedOutLocations.map((item, index) => (
              <LocationItem key={item.id} item={item} index={index} />
            ))
          ) : (
            <EmptyState message={leftSearch ? "No matching checked out locations" : "No checked out locations"} />
          )}
        </div>
      </div>

      {/* Transfer buttons */}
      <div className="flex flex-row md:flex-col items-center justify-center gap-3 md:gap-4 md:w-16 flex-shrink-0 py-2 md:py-0">
        <Tooltip content={!hasSelectedCheckedOut || locationChatProcessingId === "syncing" ? "" : "Check In Selected Locations"} position="top">
          <BtnComponent
            text="→"
            size="sm"
            btnType="primary"
            onClick={handleMoveToCheckedIn}
            disabled={!hasSelectedCheckedOut || locationChatProcessingId === "syncing"}
            className="px-4 py-2 md:px-3 md:py-1 text-lg font-bold min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0"
          />
        </Tooltip>
        <Tooltip content={!hasSelectedCheckedIn || locationChatProcessingId === "syncing" ? "" : "Check Out Selected Locations"} position="top">
          <BtnComponent
            text="←"
            size="sm"
            btnType="primary"
            onClick={handleMoveToCheckedOut}
            disabled={!hasSelectedCheckedIn || locationChatProcessingId === "syncing"}
            className="px-4 py-2 md:px-3 md:py-1 text-lg font-bold min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0"
          />
        </Tooltip>
      </div>

      {/* Checked In Locations */}
      <div className="flex flex-col flex-1 min-w-0 w-full border rounded-lg bg-white min-h-[220px] md:min-h-0">
        <div className="bg-green-50 px-3 sm:px-4 py-3 border-b rounded-t-lg flex-shrink-0">
          <h3 className="text-sm font-semibold text-green-700 mb-2">
            Checked In Locations ({filteredCheckedInLocations.length})
          </h3>
          <input
            type="text"
            value={rightSearch}
            onChange={(e) => setRightSearch(e.target.value)}
            placeholder="Search checked in..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
            aria-label="Search checked in locations"
          />
        </div>
        <div className="px-3 py-2 border-b flex-shrink-0">
          <CheckBox
            title="Select All"
            name={`select_all_checked_in_locations`}
            control={control}
            onChange={(e) => handleSelectAllCheckedInLocations(e.target.checked, filteredCheckedInLocations)}
            checked={
              filteredCheckedInLocations.length > 0 &&
              filteredCheckedInLocations.every(location => selectedLocations.includes(location.id))
            }
          />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 ">
          {filteredCheckedInLocations.length > 0 ? (
            filteredCheckedInLocations.map((item, index) => (
              <LocationItem key={item.id} item={item} index={index} />
            ))
          ) : (
            <EmptyState message={rightSearch ? "No matching checked in locations" : "No checked in locations"} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DualListBoxLocations; 